import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Divider,
  Grid,
} from "@mui/material";
import { Home } from "@mui/icons-material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";

import {
  Visibility,
  VisibilityOff,
  Apartment,
  Person,
  CreditCard,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  AlternateEmail,
  Lock,
  BadgeOutlined,
  BusinessCenter,
  MarkEmailRead,
  Refresh,
} from "@mui/icons-material";
import { Chip } from "@mui/material";

import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { GoogleLogin } from "@react-oauth/google";

import CustomCard from "../../components/common/CustomCard";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomButton from "../../components/common/CustomButton";

import { CompanyRegistrationSchema, FreelancerRegistrationSchema } from "../../components/validator/Common/RegistrationValidator";

import { clientApi } from "../../api/tenantApi";
import { subscriptionApi } from "../../api/subscriptionApi";


import CMNBIcon from "../../assets/images/merubot_logo.png";

/* ───────────────────────────────────────────────────────────── */
/* ANIMATION */
/* ───────────────────────────────────────────────────────────── */

const slideFade = (x) => keyframes`
  from {
    opacity: 0;
    transform: translateX(${x}px) translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
`;

/* ───────────────────────────────────────────────────────────── */
/* CONSTANTS */
/* ───────────────────────────────────────────────────────────── */

const REGISTRATION_TYPES = [
  {
    type: "company",
    title: "Company Workspace",
    subtitle: "For organizations, teams and recruiters",
    Icon: Apartment,
  },
  {
    type: "freelancer",
    title: "Personal Workspace",
    subtitle: "For freelancers and independent professionals",
    Icon: BadgeOutlined,
  },
];

const COMPANY_STEPS = [
  {
    label: "Account Setup",
    icon: BusinessCenter,
  },
  {
    label: "Choose Plan",
    icon: CreditCard,
  },
];

const FREELANCER_STEPS = [
  {
    label: "Account Setup",
    icon: Person,
  },
  {
    label: "Choose Plan",
    icon: CreditCard,
  },
];

const ACCOUNT_FIELDS = [
  {
    key: "full_name",
    label: "Full Name ",
    Icon: Person,
    placeholder: "John Doe",
  },
  {
    key: "email",
    label: "Email",
    Icon: AlternateEmail,
    placeholder: "john@company.com",
    type: "email",
  },
  {
    key: "password",
    label: "Password",
    Icon: Lock,
    placeholder: "Minimum 13 characters",
    type: "password",
  },
  {
    key: "confirm_password",
    label: "Confirm Password",
    Icon: Lock,
    placeholder: "Re-enter password",
    type: "password",
  },
];

/* ───────────────────────────────────────────────────────────── */
/* STEP INDICATOR */
/* ───────────────────────────────────────────────────────────── */

const StepIndicator = ({ steps, current }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1,
      }}
    >
      {steps.map(({ label, icon: Icon }, index) => {
        const active = index === current;
        const completed = index < current;

        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Stack spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all .25s ease",
                  bgcolor: completed
                    ? "primary.main"
                    : active
                      ? alpha("#6366f1", 0.12)
                      : "background.paper",
                  border: `1px solid ${active || completed ? alpha("#6366f1", 0.45) : "rgba(0,0,0,0.08)"
                    }`,
                  boxShadow: active ? `0 0 0 6px ${alpha("#6366f1", 0.08)}` : "none",
                }}
              >
                {completed ? (
                  <CheckCircle
                    sx={{
                      fontSize: 14,
                      color: "#fff",
                    }}
                  />
                ) : (
                  <Icon
                    sx={{
                      fontSize: 14,
                      color: active ? "primary.main" : "text.disabled",
                    }}
                  />
                )}
              </Box>

              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: active ? "text.primary" : "text.secondary",
                }}
              >
                {label}
              </Typography>
            </Stack>

            {index < steps.length - 1 && (
              <Box
                sx={{
                  width: 150,
                  height: 3,
                  borderRadius: 900,
                  mx: 1,
                  mb: 3.5,
                  bgcolor: completed ? "primary.main" : "divider",
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/* ───────────────────────────────────────────────────────────── */
/* MAIN PAGE */
/* ───────────────────────────────────────────────────────────── */

const TenantRegisterPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const effectCalled = useRef(false);

  const [regType, setRegType] = useState(null);
  const [step, setStep] = useState(0);
  const [animDir, setAnimDir] = useState("forward");
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [planDetail, setPlanDetail] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const [showPwd, setShowPwd] = useState({ password: false, confirm_password: false });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const [form, setForm] = useState({
    company_name: "",
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
    selectedPlanId: "",
  });
  const [errors, setErrors] = useState({
    company_name: "",
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  // ── OTP state ────────────────────────────────────────────────────────────
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpSendLocked, setotpSendLocked] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpLocked, setOtpLocked] = useState(false);           // true after max attempts
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(null); // null = unknown
  const resendIntervalRef = useRef(null);

  const startResendTimer = () => {
    setResendTimer(60);
    clearInterval(resendIntervalRef.current);
    resendIntervalRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendOtp = async () => {
    const trimmedEmail = form.email.trim().toLowerCase();
    const trimmedFullName = form.full_name.trim().replace(/\s+/g, " ");

    if (!trimmedEmail || !trimmedFullName) {
      showSnack("Missing email or full name", "error");
      return;
    }

    try {
      setOtpSending(true);
      const res = await clientApi.sendRegistrationOtp({
        email: trimmedEmail,
        user_type: regType,
        full_name: trimmedFullName,
      });
      if (res?.status === 200 || res?.success) {
        startResendTimer();
        // Reset any previous lockout / attempt state
        setOtpLocked(false);
        setOtpAttemptsLeft(null);
        setOtpError("");
        setOtpValue("");
        showSnack(`OTP sent to ${trimmedEmail}`, "success");
      } else if (res?.status === 409) {
        showSnack(res?.message || "Email already registered", "error");
        setotpSendLocked(false)
      } else {
        showSnack(res?.message || "Failed to send OTP. You can retry below.", "warning");
        setotpSendLocked(true)
      }
    } catch (error) {
      showSnack("Failed to send OTP. Please try again.", "error");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpLocked) return;
    const trimmedOtp = otpValue.trim();
    setOtpValue(trimmedOtp);
    const trimmedEmail = form.email.trim().toLowerCase();

    if (!trimmedOtp || trimmedOtp.length < 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }
    try {
      setOtpLoading(true);
      setOtpError("");
      const res = await clientApi.verifyRegistrationOtp({
        email: trimmedEmail,
        otp: trimmedOtp,
      });

      if (res?.status === 200 || res?.success) {
        setEmailVerified(true);
        setOtpLocked(false);
        setOtpAttemptsLeft(null);
        setOtpError("");
        showSnack("Email verified successfully!", "success");
      } else if (res?.status === 429 || res?.data?.locked) {
        // Account locked — too many attempts
        setOtpLocked(true);
        setOtpAttemptsLeft(0);
        setOtpValue("");
        setOtpError(
          "Too many incorrect attempts. Please request a new OTP after 15 minutes."
        );
      } else {
        // Wrong OTP — show remaining attempts if provided
        const left = res?.data?.attempts_left ?? null;
        setOtpAttemptsLeft(left);
        const attemptsMsg =
          left !== null
            ? ` ${left} attempt${left !== 1 ? "s" : ""} remaining.`
            : "";
        setOtpError((res?.message || "Invalid OTP. Please try again.") + attemptsMsg);
        setOtpValue("");
      }
    } catch {
      setOtpError("Something went wrong. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const showSnack = (message, type = "info") => {
    setSnack({
      open: true,
      message,
      severity: type,
    });
  };

  const steps = regType === "company" ? COMPANY_STEPS : FREELANCER_STEPS;
  const isLastStep = step === steps.length - 1;
  const isDetailsStep = step === 0;

  /* ───────────────────────────────────────── */
  /* HELPER FUNCTIONS */
  /* ───────────────────────────────────────── */

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const res = await subscriptionApi.getRegistrationPlan();

      if (res?.status !== 200 || !Array.isArray(res?.data)) {
        showSnack(res?.message || "Could not load subscription plans", "error");
        return;
      }

      const plans = res.data;
      if (!plans.length) {
        showSnack("No subscription plans available", "error");
        return;
      }

      const freePlan = res.data.find((plan) =>
        plan?.plan_name?.toLowerCase().includes("free")
      );

      if (!freePlan) {
        showSnack("Free subscription plan not available", "error");
        return;
      }
      setPlanDetail(freePlan);
      setForm((prev) => ({
        ...prev,
        selectedPlanId: freePlan.id,
      }));
    } catch (error) {
      // console.error("Plan fetch failed:", error);
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    if (effectCalled.current) return;
    effectCalled.current = true;
    fetchPlans();
  }, []);

  const getFieldsForCurrentStep = () => {
    if (regType === "company") {
      return ["company_name", "full_name", "email", "password", "confirm_password"];
    }
    return ["full_name", "email", "password", "confirm_password"];
  };

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "email") {
      value = value?.trim()?.toLowerCase();
    } else if (field === "password" || field === "confirm_password") {
      value = value?.trim();
    }
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);

    if (!String(value).trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const schema = regType === "company" ? CompanyRegistrationSchema : FreelancerRegistrationSchema;

    try {
      schema.validateSyncAt(field, updatedForm);
      setErrors((prev) => ({ ...prev, [field]: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [field]: error.message }));
    }

    if (field === "password" && updatedForm.confirm_password) {
      try {
        schema.validateSyncAt("confirm_password", updatedForm);
        setErrors((prev) => ({ ...prev, confirm_password: "" }));
      } catch (err) {
        setErrors((prev) => ({ ...prev, confirm_password: err.message }));
      }
    }
  };

  const validate = (formData = form) => {
    if (!isDetailsStep) {
      if (isLastStep && !captcha) {
        showSnack("Please complete captcha", "warning");
        return false;
      }
      return true;
    }

    const schema = regType === "company" ? CompanyRegistrationSchema : FreelancerRegistrationSchema;
    try {
      schema.validateSync(formData, { abortEarly: false });
      setErrors({});
    } catch (error) {
      const newErrors = {};
      if (error.inner) {
        error.inner.forEach((err) => {
          if (!newErrors[err.path]) {
            newErrors[err.path] = err.message;
          }
        });
      }
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return false;
    }

    if (isLastStep && !captcha) {
      showSnack("Please complete captcha", "warning");
      return false;
    }

    return true;
  };

  const goNext = async () => {
    const trimmedEmail = form.email.trim().toLowerCase();
    const trimmedFullName = form.full_name.trim().replace(/\s+/g, " ");
    const trimmedPassword = form.password.trim();
    const trimmedConfirmPassword = form.confirm_password.trim();
    const trimmedCompanyName = form.company_name.trim().replace(/\s+/g, " ");

    const normalizedForm = {
      ...form,
      email: trimmedEmail,
      full_name: trimmedFullName,
      password: trimmedPassword,
      confirm_password: trimmedConfirmPassword,
      company_name: trimmedCompanyName,
    };

    setForm(normalizedForm);

    // Existing frontend validation
    if (!validate(normalizedForm)) return;

    try {
      setLoading(true);

      // Check email/workspace availability
      const response = await clientApi.checkAvailability({
        user_type: regType,
        email: normalizedForm.email,
      });

      if (response?.status === 200) {
        setAnimDir("forward");
        setStep((prev) => prev + 1);
        setEmailVerified(false);
        setOtpValue("");
        setOtpError("");

        // Fire-and-forget OTP send
        sendOtp(normalizedForm);

        return;
      }

      // Handle business errors (409, 400, etc.)
      showSnack(
        response?.message ||
        response?.data?.message ||
        "Unable to validate the provided information.",
        "error"
      );
    } catch (error) {
      // console.log(error)
      showSnack(
        error?.response?.data?.message ||
        "Unable to validate the provided information.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setAnimDir("back");
    setStep((prev) => prev - 1);
    // Reset OTP state when going back so a fresh OTP is sent on re-entry
    setEmailVerified(false);
    setOtpValue("");
    setOtpError("");
    clearInterval(resendIntervalRef.current);
    setResendTimer(0);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearInterval(resendIntervalRef.current);
  }, []);

  const handleRegister = async () => {
    if (loading) return;

    const trimmedEmail = form.email.trim().toLowerCase();
    const trimmedFullName = form.full_name.trim().replace(/\s+/g, " ");
    const trimmedPassword = form.password.trim();
    const trimmedConfirmPassword = form.confirm_password.trim();
    const trimmedCompanyName = form.company_name.trim().replace(/\s+/g, " ");

    const normalizedForm = {
      ...form,
      email: trimmedEmail,
      full_name: trimmedFullName,
      password: trimmedPassword,
      confirm_password: trimmedConfirmPassword,
      company_name: trimmedCompanyName,
    };
    setForm(normalizedForm);

    if (!validate(normalizedForm)) return;

    try {
      setLoading(true);
      const payload = {
        full_name: trimmedFullName,
        email: trimmedEmail,
        password: trimmedPassword,
        plan_id: planDetail?.id,
        recaptcha_token: captcha,
      };

      let response;
      if (regType === "company") {
        response = await clientApi.registerCompany({
          ...payload,
          company_name: trimmedCompanyName,
        });
      } else {
        response = await clientApi.registerFreelancer(payload);
      }

      if (response?.status === 201) {
        setSnack({
          open: true,
          message: "Account created successfully. Redirecting to login...",
          severity: "success",
        });
        setTimeout(() => {
          navigate("/email/redirect");
        }, 2500);
      } else {
        showSnack(response?.message || "Registration failed", "error");
      }
    } catch {
      showSnack("Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ───────────────────────────────────────── */
  /* REGISTRATION TYPE PAGE */
  /* ───────────────────────────────────────── */

  if (!regType) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 2,
          background: theme.palette.background.grad,
        }}
      >
        <CustomCard
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 600,
            textAlign: "center",
            pt: 3,
            pb: 3,
            pl: 3,
            pr: 3,
            borderRadius: 3,
            boxShadow: theme.shadows[6],
            background: theme.palette.background.paper,
          }}
        >
          {/* BACK TO HOME PILL BUTTON */}
          <Tooltip title="Home">
            <IconButton
              onClick={() => navigate("/", { replace: true })}
              size="small"
              sx={{
                position: "absolute",
                top: 15,
                left: 15,
                color: "primary.main",
                // bgcolor: alpha("#6366f1", 0.08),
                "&:hover": {
                  bgcolor: alpha("#6366f1", 0.16)
                },
              }}
            >
              <Home sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          </Tooltip>

          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src={CMNBIcon}
              alt="MeruBot"
              sx={{
                height: 60,
                objectFit: "contain",
                mx: "auto",
                display: "block"
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: theme.palette.primary.main,
              }}
            >
              Create your workspace
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary",
                fontSize: "0.85rem"
              }}>
              Choose the setup that best matches how you work.
            </Typography>
          </Box>

          <Stack spacing={2}>
            {REGISTRATION_TYPES.map(({ type, title, subtitle, Icon }) => (
              <Box
                key={type}
                onClick={() => {
                  setRegType(type);
                  setStep(0);
                }}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: `1px solid ${alpha("#6366f1", 0.08)}`,
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  cursor: "pointer",
                  transition: "all .25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: alpha("#6366f1", 0.35),
                    boxShadow: "0 12px 30px rgba(99,102,241,0.10)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha("#6366f1", 0.08),
                  }}
                >
                  <Icon sx={{ color: "primary.main", fontSize: 28 }} />
                </Box>
                <Box flex={1}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    {title}
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.9rem" }}>
                    {subtitle}
                  </Typography>
                </Box>
                <ArrowForward sx={{ color: "text.disabled" }} />
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography align="center" sx={{ fontSize: "0.92rem", color: "text.secondary" }}>
            Already have an account?{" "}
            <Box
              component="span"
              onClick={() => navigate("/email/redirect")}
              sx={{ color: "primary.main", fontWeight: 700, cursor: "pointer" }}
            >
              Sign in
            </Box>
          </Typography>
        </CustomCard>

        <CustomSnackbar
          {...snack}
          autoHideDuration={3000}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        />
      </Box>
    );
  }

  /* ───────────────────────────────────────── */
  /* MAIN FORM PAGE */
  /* ───────────────────────────────────────── */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1,
        py: 1,
        background: theme.palette.background.grad,
      }}
    >
      <CustomCard
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 550,
          textAlign: "center",
          pt: 3,
          pb: 3,
          pl: 3,
          pr: 3,
          borderRadius: 3,
          boxShadow: theme.shadows[6],
          background: theme.palette.background.paper,
        }}
      >
        {/* BACK TO HOME PILL BUTTON */}
        <Tooltip title="Home">
          <IconButton
            onClick={() => navigate("/", { replace: true })}
            size="small"
            sx={{
              position: "absolute",
              top: 15,
              left: 15,
              color: "primary.main",
              // bgcolor: alpha("#6366f1", 0.08),
              "&:hover": {
                bgcolor: alpha("#6366f1", 0.16)
              },
            }}
          >
            <Home sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Tooltip>

        {/* HEADER */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="img"
            src={CMNBIcon}
            alt="MeruBot"
            sx={{
              height: 60,
              objectFit: "contain",
              mx: "auto",
              display: "block"
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: theme.palette.primary.main,
            }}
          >
            {isDetailsStep ? "Create your account" : "Choose your plan"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "text.secondary",
              fontSize: "0.85rem"
            }}
          >
            {regType === "company"
              ? "Set up your organization workspace"
              : "Create your personal workspace"}
          </Typography>
        </Box>

        {/* STEPPER */}
        {/* <StepIndicator steps={steps} current={step} /> */}
        {/* FORM CONTENT */}
        <Box
          key={`${step}-${regType}`}
          sx={{
            animation: `${slideFade(animDir === "forward" ? 24 : -24)} .3s ease both`,
          }}
        >
          {/* ACCOUNT STEP */}
          {isDetailsStep && (
            <>
              {regType === "company" && (
                <Box mb={1}>
                  <CustomInput
                    label="Company Name"
                    name="company_name"
                    size="small"
                    required
                    fullWidth
                    value={form.company_name}
                    onChange={handleChange("company_name")}
                    placeholder="CyberMeru Technologies"
                    error={!!errors.company_name}
                    helperText={errors.company_name || " "}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Apartment fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {/* GOOGLE */}
              {regType === "freelancer" && (
                <Box mb={3}>
                  <GoogleLogin
                    width="100%"
                    onSuccess={async (credentialResponse) => {
                      try {
                        setLoading(true);
                        const response = await clientApi.registerGoogleFreelancer({
                          token: credentialResponse.credential,
                        });
                        if (response?.status === 200) {
                          showSnack("Login successful", "success");
                          setTimeout(() => {
                            navigate("/email/redirect");
                          }, 800);
                        }
                        // console.log("response", response)
                        showSnack(response?.message || "something went wrong!", "error")
                      } catch {
                        showSnack("Unable to continue with Google", "error");
                      } finally {
                        setLoading(false);
                      }
                    }}
                  />
                  <Divider sx={{ my: 3 }}>
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.disabled", fontWeight: 600 }}
                    >
                      OR CONTINUE WITH EMAIL
                    </Typography>
                  </Divider>
                </Box>
              )}

              {/* INPUTS */}
              {ACCOUNT_FIELDS.map(({ key, label, Icon, placeholder, type }) => {
                const isPassword = type === "password";
                return (
                  <Box key={key} mb={1}>
                    <CustomInput
                      label={label}
                      name={key}
                      size="small"
                      {...{
                        tooltip: key === "password" ? "Password must be at least 13 characters long and contain at least one uppercase letter, one lowercase letter, one special character and one number." : undefined
                      }}
                      required
                      fullWidth
                      value={form[key]}
                      onChange={handleChange(key)}
                      placeholder={placeholder}
                      error={!!errors[key]}
                      helperText={errors[key] || " "}
                      type={isPassword ? (showPwd[key] ? "text" : "password") : type}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon fontSize="small" />
                          </InputAdornment>
                        ),
                        ...(isPassword && {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setShowPwd((prev) => ({ ...prev, [key]: !prev[key] }))
                                }
                              >
                                {showPwd[key] ? (
                                  <VisibilityOff fontSize="small" />
                                ) : (
                                  <Visibility fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }),
                      }}
                    />
                  </Box>
                );
              })}
            </>
          )}

          {/* Plan step — Free plan only */}
          {isLastStep && (
            <Box>
              {/* Alert */}
              <Box mb={2}>
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    border: emailVerified
                      ? "1.5px solid #22c55e"
                      : "1.5px solid",
                    borderColor: emailVerified ? "#22c55e" : "divider",
                    bgcolor: emailVerified
                      ? alpha("#22c55e", 0.05)
                      : alpha("#6366f1", 0.03),
                    transition: "border-color .3s, background .3s",
                  }}
                >
                  {/* Header */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <MarkEmailRead
                      sx={{ color: emailVerified ? "#22c55e" : "primary.main", fontSize: 20 }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: emailVerified ? "#16a34a" : "text.primary",
                      }}
                    >
                      {emailVerified ? "Email Verified ✓" : "Verify Your Email"}
                    </Typography>
                  </Box>

                  {emailVerified ? (
                    <Typography sx={{ fontSize: "0.85rem", color: "#16a34a" }}>
                      Your email <strong>{form.email}</strong> has been verified.
                    </Typography>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: "0.83rem", color: "text.secondary", mb: 2 }}>
                        A 6-digit code was sent to{" "}
                        <strong>
                          {form.email.replace(
                            /^(.{1,3})(.*)(@.*)$/,
                            (_, a, b, c) => a + "*".repeat(Math.max(b.length, 2)) + c
                          )}
                        </strong>
                      </Typography>

                      {/* 6-digit OTP input */}
                      <Box sx={{ display: "flex", gap: 1, mb: 1.5, justifyContent: "center" }}>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <Box
                            key={i}
                            component="input"
                            id={`otp-digit-${i}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            disabled={otpLocked}
                            value={otpValue[i] || ""}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/, "");
                              const arr = otpValue.split("");
                              arr[i] = val;
                              const next = arr.join("").slice(0, 6);
                              setOtpValue(next);
                              setOtpError("");
                              if (val && i < 5) {
                                document.getElementById(`otp-digit-${i + 1}`)?.focus();
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Backspace" && !otpValue[i] && i > 0) {
                                document.getElementById(`otp-digit-${i - 1}`)?.focus();
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pasted = e.clipboardData
                                .getData("text")
                                .replace(/\D/g, "")
                                .slice(0, 6);
                              setOtpValue(pasted);
                              const nextIdx = Math.min(pasted.length, 5);
                              document.getElementById(`otp-digit-${nextIdx}`)?.focus();
                            }}
                            sx={{
                              width: 40,
                              height: 46,
                              textAlign: "center",
                              fontSize: "1.2rem",
                              fontWeight: 700,
                              border: otpError
                                ? "1.5px solid #ef4444"
                                : "1.5px solid",
                              borderColor: otpError ? "#ef4444" : "divider",
                              borderRadius: "8px",
                              outline: "none",
                              bgcolor: otpLocked ? "action.disabledBackground" : "background.paper",
                              color: "text.primary",
                              caretColor: "primary.main",
                              opacity: otpLocked ? 0.5 : 1,
                              cursor: otpLocked ? "not-allowed" : "text",
                              transition: "border-color .2s",
                              "&:focus": {
                                borderColor: "primary.main",
                                boxShadow: `0 0 0 3px ${alpha("#6366f1", 0.12)}`,
                              },
                            }}
                          />
                        ))}
                      </Box>

                      {/* Error / lockout text */}
                      {otpLocked ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.8,
                            bgcolor: alpha("#ef4444", 0.08),
                            border: "1px solid",
                            borderColor: alpha("#ef4444", 0.3),
                            borderRadius: 2,
                            px: 1.5,
                            py: 1,
                            mb: 1.5,
                          }}
                        >
                          <Typography sx={{ fontSize: "0.78rem", color: "#ef4444", fontWeight: 600 }}>
                            🔒 Account locked after 3 incorrect attempts.
                            Please request a new OTP to continue.
                          </Typography>
                        </Box>
                      ) : otpError ? (
                        <Typography sx={{ fontSize: "0.78rem", color: "#ef4444", mb: 1, textAlign: "center" }}>
                          {otpError}
                        </Typography>
                      ) : null}

                      {/* Verify button */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
                        <Box
                          component="button"
                          onClick={handleVerifyOtp}
                          disabled={otpLoading || otpValue.length < 6 || otpLocked}
                          sx={{
                            px: 2.5,
                            py: 0.9,
                            borderRadius: "8px",
                            border: "none",
                            bgcolor: "primary.main",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            cursor: otpLoading || otpValue.length < 6 ? "not-allowed" : "pointer",
                            opacity: otpLoading || otpValue.length < 6 ? 0.6 : 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            transition: "opacity .2s",
                            "&:hover": { opacity: 0.85 },
                          }}
                        >
                          {otpLoading ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <CheckCircle sx={{ fontSize: 15 }} />
                          )}
                          Verify OTP
                        </Box>

                        {/* Resend */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          {resendTimer > 0 ? (
                            <Typography sx={{ fontSize: "0.78rem", color: "text.disabled" }}>
                              Resend in {resendTimer}s
                            </Typography>
                          ) : (
                            <Tooltip title={otpSendLocked ? "Account Locked for sometime" : ""}>
                              <Box
                                component="button"
                                onClick={sendOtp}
                                disabled={otpSending || otpSendLocked}
                                sx={{
                                  background: "none",
                                  border: "none",
                                  color: "primary.main",
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  cursor: otpSending ? "wait" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                  p: 0,
                                  textDecoration: "underline",
                                  "&:hover": { opacity: 0.75 },
                                }}
                              >
                                {otpSending ? (
                                  <CircularProgress size={10} color="inherit" />
                                ) : (
                                  <Refresh sx={{ fontSize: 13 }} />
                                )}
                                Resend OTP
                              </Box>
                            </Tooltip>

                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>

                {plansLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={22} color="primary" />
                  </Box>
                ) : !planDetail ? (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    Free plan could not be loaded. Please refresh the page or contact support.
                  </Typography>
                ) : (
                  <Box mb={2}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "text.disabled",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        mb: 1.5,
                      }}
                    >
                      <CreditCard sx={{ fontSize: 13 }} />
                      Subscription Plan
                      <Chip
                        label={String(
                          planDetail.plan_name || planDetail.planName || "Free"
                        ).toUpperCase()}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Typography>


                    <Box
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: "primary.main",
                        bgcolor: "action.selected",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Price:
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.light">
                          Free
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Credits Included:
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {planDetail.credit}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Recruiters Allowed:
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {planDetail.allowed_recruiter_count}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Plan Expired In:
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {planDetail.plan_expired_in_days != null
                            ? `${planDetail.plan_expired_in_days} days`
                            : "No validity"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    theme="light"
                    size="normal"
                    width="1000px"
                    onChange={setCaptcha}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* ACTIONS */}
        <Stack direction="row" spacing={1.5} mt={1}>
          {step > 0 && (
            <CustomButton
              fullWidth
              variant="outlined"
              onClick={goBack}
              startIcon={<ArrowBack />}
              sx={{ minWidth: 120, height: 40, borderRadius: 2.5, fontWeight: 700, fontSize: "0.8rem", textTransform: "none" }}
            >
              Back
            </CustomButton>
          )}
          <CustomButton
            fullWidth
            onClick={isLastStep ? handleRegister : goNext}
            disabled={
              loading || Object.values(errors).some((v) => v) ||
              (isDetailsStep && getFieldsForCurrentStep().some((field) => !form[field]?.trim())) ||
              (isLastStep && !captcha) ||
              (isLastStep && (!planDetail || plansLoading)) ||
              (isLastStep && !emailVerified) ||
              (isLastStep && !captcha)
            }
            endIcon={loading ? null : isLastStep ? <CheckCircle /> : <ArrowForward />}
            sx={{
              ...(isLastStep && !emailVerified && {
                height: 40,
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: "0.8rem",
                "&:hover": { bgcolor: "grey.300" },
              }),
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isLastStep ? (
              !emailVerified ? "Verify to continue" : "Create Account"
            ) : (
              "Continue"
            )}
          </CustomButton>
        </Stack>

        {/* FOOTER */}
        <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", textAlign: "center" }}>
          <Typography sx={{ mt: 1, fontSize: "0.8rem", color: theme.palette.text.secondary }}>
            Already have an account?{" "}
            <Box
              component="span"
              onClick={() => navigate("/email/redirect")}
              sx={{ color: theme.palette.primary.main, fontWeight: 600, cursor: "pointer" }}
            >
              Sign in
            </Box>
          </Typography>

          <Typography
            onClick={() => {
              setRegType(null);
              setStep(0);
            }}
            sx={{
              mt: 0.5,
              fontSize: "0.82rem",
              color: "text.disabled",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
          >
            Change workspace type
          </Typography>
        </Box>
      </CustomCard>

      <CustomSnackbar
        {...snack}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default TenantRegisterPage;